import { useEffect, useRef, useState } from "react";

const GOALS = [
  { ar: "سدّ فجوة الكفاءات في القطاع غير الربحي", tag: "الحاجة" },
  { ar: "رفع جاهزية الطلاب الجامعيين لسوق العمل التنموي", tag: "التمكين" },
  { ar: "بناء قناة مستدامة لاستقطاب المواهب المؤهّلة للجمعيات", tag: "الاستدامة" },
  { ar: "تعزيز الشراكة بين الجامعات والجمعيات", tag: "الشراكة" },
  { ar: "نشر ثقافة التطوّع التخصّصي والاحترافي", tag: "الثقافة" },
  { ar: "المساهمة في تحقيق مستهدفات رؤية ٢٠٣٠", tag: "الرؤية" },
  { ar: "تمكين الجمعيات من استثمار المشروعات الجامعية التطبيقية", tag: "الاستثمار" },
];

const AR_INDEX = ["٠١", "٠٢", "٠٣", "٠٤", "٠٥", "٠٦", "٠٧"];

/**
 * "عن عماد" — an editorial, out-of-the-box section:
 * - Dark navy canvas with grain + soft radial glow
 * - Oversized display glyph as watermark ("ع")
 * - Two-column asymmetric grid: editorial paragraph with drop-cap on the right,
 *   numbered goal ribbons on the left arranged in a staggered vertical path.
 * - Rose thread connecting the seven numbered nodes.
 * - Rotates focused goal on hover / keyboard focus.
 */
export default function AboutSection() {
  const [active, setActive] = useState(0);
  const trackRef = useRef(null);

  // Auto-cycle the highlighted goal for gentle motion
  useEffect(() => {
    const t = setInterval(() => setActive((p) => (p + 1) % GOALS.length), 3200);
    return () => clearInterval(t);
  }, []);

  return (
    <section
      id="about"
      className="relative overflow-hidden bg-slate-950 text-white py-24 lg:py-32"
      data-testid="about-section"
      aria-labelledby="about-title"
    >
      {/* Background layers */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(15,118,110,0.35),transparent_60%),radial-gradient(ellipse_at_bottom_left,rgba(190,24,93,0.25),transparent_55%)]" aria-hidden="true" />
      <div className="absolute inset-0 opacity-[0.06] mix-blend-overlay" aria-hidden="true"
        style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")" }} />

      {/* Oversized glyph watermark */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -end-8 top-4 text-[22rem] leading-none font-heading font-black text-white/[0.035] select-none"
        style={{ letterSpacing: "-0.08em" }}
      >
        ع
      </div>

      {/* Section header — asymmetric */}
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-end gap-4 mb-4">
          <span className="h-px w-16 bg-amber-400/60" aria-hidden="true" />
          <span className="text-[11px] font-bold uppercase tracking-[0.35em] text-amber-300">
            بيان عماد
          </span>
        </div>
        <h2
          id="about-title"
          className="max-w-4xl text-4xl sm:text-5xl lg:text-7xl font-heading font-black leading-[1.02] tracking-tight"
        >
          نحوّل المشروع الأكاديمي إلى
          <span className="block mt-3 bg-gradient-to-l from-amber-300 via-rose-300 to-teal-200 bg-clip-text text-transparent">
            كفاءة تُحدث الأثر.
          </span>
        </h2>
      </div>

      {/* Body */}
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 mt-16 lg:mt-24 grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
        {/* Editorial paragraph with drop-cap */}
        <article className="lg:col-span-6 lg:order-2 relative" data-testid="about-narrative">
          <div className="absolute -start-4 top-2 h-24 w-px bg-gradient-to-b from-amber-300/70 to-transparent hidden lg:block" aria-hidden="true" />
          <p className="text-lg lg:text-xl leading-[2.1] text-teal-50/95">
            <span
              className="align-baseline me-2 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-black bg-gradient-to-br from-amber-300 to-rose-400 bg-clip-text text-transparent"
            >
              برنامج
            </span>
            تنموي متكامل يحوّل المشاريع الأكاديمية لطلاب الجامعة إلى رحلة تطوير مهني هادفة،
            مصمَّم خصيصًا لسدّ فجوة الكفاءات في منظمات القطاع غير الربحي. تقوم منهجية البرنامج على
            تشخيص دقيق للاحتياجات المهنية في الجمعيات، ثم استثمار المشاريع التطبيقية الجامعية
            لتوجيه الطلاب نحو هذه الفجوات المحدّدة. يتم تقييم فجواتهم المهنية بشكل فردي، ومن ثَمّ
            إلحاقهم بمسار تدريبي إلكتروني مكثّف ومصمَّم بعناية لسدّ هذه الفجوات وضمان جاهزيتهم
            الكاملة للمهمة.
          </p>

          <div className="mt-8 p-4 rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm relative">
            <div className="absolute -top-3 start-6 px-3 py-0.5 rounded-full bg-amber-400 text-slate-900 text-[10px] font-bold tracking-widest">
              الوعد
            </div>
            <p className="text-teal-100/95 leading-loose">
              <span
                className="align-baseline me-2 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-black bg-gradient-to-br from-amber-300 to-rose-400 bg-clip-text text-transparent"
              >
                إعداد
              </span>
              كوادر شابة تمتلك الأدوات الأساسية، ونوفّر للجمعيات قناةً مستدامةً لاستقطاب المواهب والكفاءات الشابة التي تم إعدادها، ممّا يرفع من جودة الأداء والأثر في القطاع غير الربحي.
            </p>
          </div>

          <div className="mt-8 inline-flex items-center gap-3 rounded-full border border-teal-400/30 bg-teal-400/5 px-4 py-2 text-xs text-teal-100">
            <span className="h-2 w-2 rounded-full bg-teal-300 animate-pulse" aria-hidden="true" />
            متوافق مع مستهدفات <span className="font-bold text-white">رؤية ٢٠٣٠</span>
          </div>
        </article>

        {/* Numbered goals path */}
        <div className="lg:col-span-6 lg:order-1 relative" ref={trackRef}>
          <div className="flex items-center gap-3 mb-8">
            <span className="text-[11px] font-bold uppercase tracking-[0.35em] text-white/60">
              أهدافنا السبعة
            </span>
            <span className="h-px flex-1 bg-white/10" aria-hidden="true" />
            <span className="text-xs text-white/40 font-mono">07 / {String(active + 1).padStart(2, "0")}</span>
          </div>

          <ol
            className="relative space-y-3"
            data-testid="about-goals"
            aria-label="أهداف عماد السبعة"
          >
            {/* connecting thread */}
            <span
              aria-hidden="true"
              className="absolute start-6 top-4 bottom-4 w-px bg-gradient-to-b from-amber-300/40 via-rose-300/25 to-teal-300/20"
            />

            {GOALS.map((g, i) => {
              const isActive = i === active;
              // stagger every other item to break the grid
              const shift = i % 2 === 0 ? "lg:ms-0" : "lg:ms-8";
              return (
                <li
                  key={i}
                  className={`relative ${shift}`}
                  onMouseEnter={() => setActive(i)}
                  onFocus={() => setActive(i)}
                >
                  <div
                    tabIndex={0}
                    className={`group relative flex items-start gap-4 rounded-2xl border p-4 lg:p-5 transition-all duration-500 outline-none focus-visible:ring-2 focus-visible:ring-amber-300/60 ${
                      isActive
                        ? "border-amber-300/60 bg-white/[0.06] translate-x-0 lg:-translate-x-1 shadow-[0_10px_40px_-15px_rgba(251,191,36,0.35)]"
                        : "border-white/10 bg-white/[0.02] hover:bg-white/[0.04]"
                    }`}
                    data-testid={`about-goal-${i}`}
                  >
                    {/* number node */}
                    <div
                      className={`shrink-0 h-12 w-12 rounded-xl grid place-items-center font-heading font-black text-lg transition-all duration-500 ${
                        isActive
                          ? "bg-gradient-to-br from-amber-300 to-rose-400 text-slate-900 rotate-[-4deg] scale-105"
                          : "bg-white/5 text-white/70 border border-white/10"
                      }`}
                      aria-hidden="true"
                    >
                      {AR_INDEX[i]}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className={`text-[10px] font-bold uppercase tracking-[0.3em] mb-1 transition-colors ${isActive ? "text-amber-300" : "text-white/40"}`}>
                        {g.tag}
                      </div>
                      <div className={`text-base lg:text-lg font-heading font-bold leading-snug transition-colors ${isActive ? "text-white" : "text-white/80"}`}>
                        {g.ar}
                      </div>
                    </div>
                    {/* accent bar */}
                    <div
                      aria-hidden="true"
                      className={`absolute inset-y-0 end-0 w-[3px] rounded-l-full transition-all duration-500 ${
                        isActive ? "bg-gradient-to-b from-amber-300 to-rose-400" : "bg-transparent"
                      }`}
                    />
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
}
